/**
 * Shell do PWA interno da Plataforma: sidebar lateral persistente, topbar
 * "command center" e a área de conteúdo das rotas.
 *
 * Redesign R9 (segunda passada, apresentação apenas): a sidebar passa de lista
 * PLANA para **grupos com ícone, colapsáveis** (o padrão mais reconhecível do
 * TailAdmin) — maior lacuna estrutural identificada contra a R6. Cada grupo
 * fecha por padrão (menu menos poluído ao abrir a Plataforma); o colapsar/
 * expandir é só estado de apresentação (`useState`) — o `<ul>` de cada grupo
 * SEMPRE renderiza todos os seus links no DOM, só alterna a classe `hidden`,
 * então o aceite "todos os módulos de NAV_ITEMS continuam navegáveis"
 * (`tests/plataforma-shell.test.ts`) continua verificável no HTML servido,
 * sem depender de clique.
 *
 * `NAV_ITEMS` (lista plana, contrato público consumido pelo teste do shell)
 * continua existindo e é a FONTE de verdade; `NAV_GROUPS` só agrupa essas
 * mesmas entradas para a navegação visual — nenhuma rota nova, nenhuma
 * rota removida.
 *
 * Redesign "admin Positiva" (Fase 1): a sidebar passa a grafite (`bg-sidebar`,
 * nos dois temas) com grupos FIXOS de título em caixa-alta, ícone por rota,
 * pílula vermelha no item ativo e modo recolhido só com ícones (`title` +
 * `sr-only` mantêm o nome acessível e o link no HTML). Faixa de ambiente
 * simulado no topo. Os grupos deixam de ser colapsáveis (o texto acima sobre
 * `useState` por grupo vale só para a R9; o único estado agora é o da sidebar).
 *
 * Invariantes preservados do shell anterior (R6):
 *  - navegação lista TODOS os módulos entregues (NAV_ITEMS);
 *  - landmarks: <header>, <nav aria-label="Módulos da Plataforma">, <main id="conteudo">;
 *  - badge de sessão simulada com data-session-source="simulator";
 *  - nunca cor como ÚNICO indicador: o item ativo combina borda/ícone vermelho
 *    + peso de texto + aria-current; o toggle de tema tem rótulo textual.
 */
// Import explícito de `React` (necessário ao `ViewSwitcher` em JSX sob `tsx --test`, que cai no
// transform clássico); o restante do arquivo segue em `createElement`.
import React, { createElement as h, Fragment, useEffect, useState, type ReactElement, type ReactNode } from "react";
import { useAccessLevelFor } from "../auth/access.js";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { NavLink, useLocation } from "react-router-dom";
import {
  ExternalLink,
  BadgeCheck,
  Bell,
  Car,
  Contact,
  Tags,
  Warehouse,
  Check,
  ChevronDown,
  Boxes,
  ChevronsLeft,
  ChevronsRight,
  FileText,
  Gauge,
  Handshake,
  Headset,
  GitCompare,
  Image as ImageIcon,
  Landmark,
  LayoutDashboard,
  ListChecks,
  LogOut,
  type LucideIcon,
  MapPin,
  Megaphone,
  MessageSquare,
  Menu,
  Moon,
  Package,
  Percent,
  Plug,
  ScrollText,
  HandCoins,
  LineChart,
  Receipt,
  RefreshCw,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  SquareCheck,
  Store,
  Sun,
  Ticket,
  TrendingUp,
  Truck,
  Users,
  Wallet,
  Workflow,
  Bot
} from "lucide-react";
import { DEFAULT_VIEW_ID, VIEWS, getView, groupVisibleInView, isViewId, type ViewId } from "./views.js";
import { useStaffSession } from "../auth/StaffSessionBoundary.js";
import {
  applyTheme,
  currentTheme,
  persistTheme,
  resolveInitialTheme,
  toggleTheme,
  type Theme
} from "../theme/theme.js";

interface NavItem {
  to: string;
  label: string;
}

/** Rotas já entregues pelas Tasks 10–13. Fonte de verdade da navegação. */
/** Endereço público da Loja. `VITE_STORE_URL` sobrescreve (ex.: homologação); vazio explícito esconde o botão. */
const STORE_URL = ((import.meta as { env?: { VITE_STORE_URL?: string } }).env?.VITE_STORE_URL ?? "https://loja.super2eletronicos.com.br").trim();

export const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Dashboard executivo" },
  { to: "/operacional", label: "Dashboard operacional" },
  // Redesign admin, Fase 2.
  { to: "/resumo", label: "Resumo executivo" },
  // Task 12: a lista de pedidos é multicanal (Loja + WhatsApp + marketplace +
  // manual); o detalhe de cada pedido abre em /pedidos/:orderId.
  { to: "/pedidos", label: "Pedidos (todos os canais)" },
  // Redesign admin, Fase 3.
  { to: "/televendas", label: "Televendas" },
  { to: "/canais", label: "Canais de venda" },
  { to: "/canais/mercado-livre", label: "Mercado Livre" },
  { to: "/margens", label: "Margens" },
  // Task 11: catálogo em duas camadas, disponibilidade/reservas e a tela de
  // divergências item a item.
  { to: "/catalogo", label: "Catálogo" },
  { to: "/estoque", label: "Estoque" },
  // Redesign admin, Fase 4.
  { to: "/estoque/filiais", label: "Estoque único" },
  { to: "/categorias", label: "Categorias" },
  { to: "/aplicacoes", label: "Aplicações" },
  { to: "/clientes", label: "Clientes" },
  { to: "/notificacoes", label: "Notificações" },
  { to: "/disponibilidade", label: "Disponibilidade" },
  { to: "/divergencias", label: "Divergências Bling↔local" },
  { to: "/inbox", label: "Atendimento WhatsApp" },
  // Task 13: CRM 360. A visão 360 de um cliente abre em /clientes/:customerId
  // (a partir do atendimento, do pedido ou do funil); aqui ficam as telas que
  // não são de um cliente específico.
  { to: "/crm/pipeline", label: "Pipeline comercial" },
  { to: "/crm/orcamentos", label: "Orçamentos" },
  // Plano Loja/Twotrees, Fase 10: cupons de desconto do checkout da Loja.
  { to: "/cupons", label: "Cupons de desconto" },
  { to: "/crm/tarefas", label: "Tarefas e follow-ups" },
  { to: "/crm/segmentos", label: "Segmentos" },
  { to: "/crm/respostas-rapidas", label: "Respostas rápidas" },
  { to: "/crm/grupos", label: "Grupos de WhatsApp" },
  { to: "/crm/fluxos", label: "Fluxos de atendimento" },
  // Task 14: logística e expedição — a fila de separação→despacho e a visão
  // unificada de rastreio (estado interno + transportador).
  { to: "/expedicao", label: "Expedição" },
  { to: "/rastreio", label: "Rastreio" },
  { to: "/sincronizacao", label: "Sincronização" },
  { to: "/garantias", label: "Garantias / RMA" },
  // Task 16 (financeiro gerencial): conciliação de pagamentos, contas/
  // lançamentos gerenciais e rentabilidade (margem realizada). Gerencial,
  // nunca fiscal. As três rotas já existiam em App.ts (acessíveis só por
  // drill-down) sem entrada no menu — adicionadas na Fase 5 do Plano de
  // Ataque R8, sem o que elas eram órfãs: só se chegava a elas por link.
  // Contas a pagar, contas a receber (parcelas) e fluxo de caixa: derivados dos títulos, migration 0056.
  { to: "/financeiro/fluxo-de-caixa", label: "Fluxo de caixa" },
  { to: "/financeiro/contas-a-receber", label: "Contas a receber" },
  { to: "/financeiro/contas-a-pagar", label: "Contas a pagar" },
  { to: "/financeiro/conciliacao", label: "Conciliação de pagamentos" },
  { to: "/financeiro/contas", label: "Contas gerenciais" },
  { to: "/financeiro/rentabilidade", label: "Rentabilidade" },
  // Entrega 0 (R3–R5): estado das integrações (simulador/real), o que falta e
  // os feature flags — read-only, nunca mostra o valor de um segredo.
  { to: "/integracoes", label: "Integrações" },
  { to: "/auditoria", label: "Auditoria" },
  { to: "/configuracoes", label: "Configurações" },
  { to: "/configuracoes/usuarios", label: "Usuários e acessos" },
  { to: "/configuracoes/ia", label: "IA (assistente)" },
  // Plano Loja/Twotrees, Fase 8: faixa de pré-venda acima do header da Loja.
  { to: "/marketing/faixa-presale", label: "Faixa de pré-venda" },
  // Plano Loja/Twotrees, Fase 9: banners promocionais da home.
  { to: "/marketing/banners", label: "Banners" }
];

interface NavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Rotas (`NavItem.to`) desta seção, na ordem em que aparecem no menu. */
  routes: string[];
}

/**
 * Agrupamento visual de `NAV_ITEMS` (puramente de apresentação — nenhuma rota
 * nova). Cobertura das 21 rotas confirmada pelo teste do shell: se uma rota
 * ficar de fora de todo grupo, o link some do menu e o teste acusa.
 */
const NAV_GROUPS: NavGroup[] = [
  { id: "visao-geral", label: "Visão", icon: LayoutDashboard, routes: ["/", "/resumo", "/operacional"] },
  {
    id: "vendas",
    label: "Vendas",
    icon: ShoppingBag,
    routes: ["/pedidos", "/televendas", "/canais", "/canais/mercado-livre", "/margens", "/crm/pipeline", "/crm/orcamentos", "/cupons"]
  },
  {
    id: "catalogo-estoque",
    label: "Catálogo & Estoque",
    icon: Package,
    routes: ["/catalogo", "/categorias", "/aplicacoes", "/estoque", "/estoque/filiais", "/disponibilidade", "/divergencias"]
  },
  {
    id: "atendimento-crm",
    label: "Relacionamento",
    icon: MessageSquare,
    routes: ["/inbox", "/clientes", "/notificacoes", "/crm/respostas-rapidas", "/crm/grupos", "/crm/fluxos", "/crm/tarefas", "/crm/segmentos"]
  },
  {
    id: "logistica",
    label: "Logística",
    icon: Truck,
    routes: ["/expedicao", "/rastreio", "/sincronizacao", "/garantias"]
  },
  {
    id: "financeiro",
    label: "Financeiro",
    icon: Wallet,
    routes: ["/financeiro/fluxo-de-caixa", "/financeiro/contas-a-receber", "/financeiro/contas-a-pagar", "/financeiro/conciliacao", "/financeiro/contas", "/financeiro/rentabilidade"]
  },
  { id: "sistema", label: "Plataforma", icon: Settings, routes: ["/integracoes", "/auditoria", "/configuracoes", "/configuracoes/usuarios", "/configuracoes/ia", "/marketing/faixa-presale", "/marketing/banners"] }
];

/** Ícone de cada rota (o admin de referência mostra um ícone por item; recolhida, só ele). */
const ROUTE_ICONS: Record<string, LucideIcon> = {
  "/": LayoutDashboard,
  "/operacional": Gauge,
  "/resumo": Receipt,
  "/pedidos": ShoppingCart,
  "/televendas": Headset,
  "/canais": Store,
  "/canais/mercado-livre": Handshake,
  "/margens": Percent,
  "/crm/pipeline": Workflow,
  "/crm/orcamentos": FileText,
  "/cupons": Ticket,
  "/catalogo": Package,
  "/estoque": Boxes,
  "/estoque/filiais": Warehouse,
  "/categorias": Tags,
  "/aplicacoes": Car,
  "/clientes": Contact,
  "/notificacoes": Bell,
  "/disponibilidade": ListChecks,
  "/divergencias": GitCompare,
  "/inbox": MessageSquare,
  "/crm/tarefas": SquareCheck,
  "/crm/segmentos": Users,
  "/expedicao": Truck,
  "/rastreio": MapPin,
  "/sincronizacao": RefreshCw,
  "/garantias": ShieldCheck,
  "/financeiro/fluxo-de-caixa": LineChart,
  "/financeiro/contas-a-receber": HandCoins,
  "/financeiro/contas-a-pagar": Receipt,
  "/financeiro/conciliacao": Landmark,
  "/financeiro/contas": Wallet,
  "/financeiro/rentabilidade": TrendingUp,
  "/integracoes": Plug,
  "/auditoria": ScrollText,
  "/configuracoes": Settings,
  "/configuracoes/usuarios": Users,
  "/configuracoes/ia": Bot,
  "/marketing/faixa-presale": Megaphone,
  "/marketing/banners": ImageIcon
};

const COLLAPSE_KEY = "super2-sidebar-collapsed";

/** Item com sub-rota no menu (ex.: /canais e /canais/mercado-livre) só fica ativo na rota exata — senão os dois acendem. */
/** Rota ativa para fins de grupo: exata, ou prefixo quando o item não tem sub-rota própria no menu. */
function isActivePath(to: string, pathname: string): boolean {
  if (to === "/") return pathname === "/";
  if (hasNavChild(to)) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

function hasNavChild(to: string): boolean {
  return NAV_ITEMS.some((other) => other.to !== to && other.to.startsWith(`${to}/`));
}

function itemsFor(group: NavGroup, canSee: (path: string) => boolean = () => true): NavItem[] {
  return group.routes
    .map((route) => NAV_ITEMS.find((item) => item.to === route))
    .filter((item): item is NavItem => item !== undefined && canSee(item.to));
}

/** Logo do cliente — asset auto-hospedado em /public. Dimensões explícitas p/ CLS=0. */
function Brand({ collapsed }: { collapsed: boolean }): ReactElement {
  return h(
    "a",
    {
      href: "/",
      className: "flex items-center gap-3 rounded-control px-1 py-1 focus-visible:outline-none",
      "aria-label": "Super2 Eletrônicos — início"
    },
    // A sidebar é grafite nos dois temas e o logo tem texto preto: chip branco
    // neutro atrás (legibilidade, sem inventar cor de marca). Recolhida, o chip
    // encolhe e o logo é recortado para o raio vermelho (object-left).
    h(
      "span",
      {
        className:
          "grid shrink-0 place-items-center overflow-hidden rounded-control bg-white " +
          (collapsed ? "h-10 w-10 p-1" : "h-11 px-3")
      },
      h("img", {
        src: "/super2-logo.webp",
        alt: "Super2 Eletrônicos",
        width: 90,
        height: 36,
        className: collapsed ? "h-[32px] w-[32px] object-cover object-left" : "h-9 w-auto object-contain"
      })
    ),
    // Nome acessível já vem do aria-label do link; texto visível ao lado do logo estourava a coluna.
    collapsed ? null : h("span", { className: "sr-only" }, "Painel administrativo")
  );
}

/** Toggle de tema acessível: ícone + rótulo textual, aria-pressed, persiste. */
function S2ThemeToggle(): ReactElement {
  // Inicializa a partir do que o pré-paint já aplicou no <html> (sem efeito):
  // em SSR/teste não há document, então cai no resolvedor puro (light default).
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof document === "undefined") return resolveInitialTheme(null, false);
    return currentTheme(document.documentElement);
  });

  // Sincroniza o estado com o DOM após montar (o pré-paint pode ter resolvido
  // "dark" por prefers-color-scheme antes do React hidratar).
  useEffect(() => {
    setTheme(currentTheme(document.documentElement));
  }, []);

  const isDark = theme === "dark";

  function onToggle(): void {
    const next = toggleTheme(theme);
    applyTheme(document.documentElement, next);
    persistTheme(next, typeof localStorage !== "undefined" ? localStorage : null);
    setTheme(next);
  }

  return h(
    "button",
    {
      type: "button",
      onClick: onToggle,
      "aria-pressed": isDark,
      // Nome acessível textual explícito (não depende só do ícone/cor).
      "aria-label": isDark ? "Ativar tema claro" : "Ativar tema escuro",
      "data-theme-toggle": "true",
      className:
        "inline-flex min-h-[44px] items-center gap-2 rounded-control border border-border-strong " +
        "bg-surface px-3 py-2 text-sm font-medium text-body transition-colors duration-fast ease-s2 " +
        "hover:bg-surface-soft focus-visible:outline-none"
    },
    isDark ? h(Sun, { className: "h-5 w-5", "aria-hidden": true, focusable: false }) : h(Moon, { className: "h-5 w-5", "aria-hidden": true, focusable: false }),
    h("span", { className: "hidden whitespace-nowrap xl:inline" }, isDark ? "Tema claro" : "Tema escuro")
  );
}

/** Classe do NavLink, com estado ativo marcado por borda/texto (não só cor). */
function navLinkClass(collapsed: boolean) {
  return ({ isActive }: { isActive: boolean }): string => {
    const base =
      "flex min-h-[44px] items-center gap-3 rounded-control px-3 text-sm " +
      "transition-colors duration-fast ease-s2 focus-visible:outline-none " +
      (collapsed ? "justify-center px-0" : "");
    // Item ativo: pílula vermelha de marca + peso + aria-current (não só cor).
    return isActive
      ? `${base} bg-brand font-bold text-on-brand`
      : `${base} font-semibold text-sidebar-fg hover:bg-sidebar-hover hover:text-white`;
  };
}

/**
 * Seção colapsável de um grupo de navegação. O colapso é só visual — fecha
 * via `aria-hidden`/altura 0 depois de montado; o SSR (e o estado inicial do
 * cliente) sempre renderiza a lista aberta, então nenhum link some do HTML.
 */
function NavSection({
  group,
  collapsed,
  open,
  onToggle
}: {
  group: NavGroup;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
}): ReactElement {
  const levelOf = useAccessLevelFor();
  const items = itemsFor(group, (path) => levelOf(path) !== "none");
  const headingId = `nav-group-${group.id}`;
  const listId = `nav-list-${group.id}`;
  const GroupIcon = group.icon;

  // Grupos colapsáveis (acordeão): só o grupo da rota atual abre sozinho; o resto
  // abre quando o operador pede. O `ul` SEMPRE renderiza todos os links no DOM
  // (só alterna `hidden`), então "todos os módulos de NAV_ITEMS navegáveis"
  // continua verificável no HTML servido. Com a sidebar recolhida (só ícones) não
  // há acordeão: todos os ícones ficam visíveis.
  const expanded = collapsed || open;
  // Grupo sem nenhum módulo liberado para esta conta some do menu.
  if (items.length === 0) return h(Fragment);
  return h(
    "div",
    { className: "flex flex-col", "data-nav-group": group.id, "data-nav-open": expanded ? "true" : "false" },
    collapsed
      ? h("span", { id: headingId, className: "sr-only" }, group.label)
      : h(
          "button",
          {
            type: "button",
            id: headingId,
            onClick: onToggle,
            "aria-expanded": open,
            "aria-controls": listId,
            className:
              "flex min-h-[40px] w-full items-center gap-2 rounded-control px-3 text-left text-[0.72rem] font-extrabold " +
              "uppercase tracking-wider text-sidebar-muted transition-colors duration-fast ease-s2 " +
              "hover:bg-sidebar-hover hover:text-sidebar-fg focus-visible:outline-none"
          },
          h(GroupIcon, { className: "h-4 w-4 shrink-0", "aria-hidden": true, focusable: false }),
          h("span", { className: "flex-1" }, group.label),
          h(ChevronDown, {
            className: "h-4 w-4 shrink-0 transition-transform duration-fast ease-s2 " + (open ? "" : "-rotate-90"),
            "aria-hidden": true,
            focusable: false
          })
        ),
    h(
      "ul",
      {
        id: listId,
        "aria-labelledby": headingId,
        role: "list",
        hidden: !expanded,
        className: "mt-1 flex flex-col gap-1 " + (collapsed ? "" : "border-l border-sidebar-border ml-5 pl-2")
      },
      items.map((item) => {
        const Icon = ROUTE_ICONS[item.to] ?? group.icon;
        return h(
          "li",
          { key: item.to },
          h(
            NavLink,
            { to: item.to, end: item.to === "/" || hasNavChild(item.to), className: navLinkClass(collapsed), title: collapsed ? item.label : undefined },
            h(Icon, { className: "h-5 w-5 shrink-0", "aria-hidden": true, focusable: false }),
            h("span", { className: collapsed ? "sr-only" : "min-w-0 flex-1 leading-tight" }, item.label)
          )
        );
      })
    )
  );
}

const VIEW_STORAGE_KEY = "super2-view";

/** Lê a visão salva (se válida); sem valor salvo ou inválido, cai no default (completa). */
function readStoredView(): ViewId {
  if (typeof localStorage === "undefined") return DEFAULT_VIEW_ID;
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    return isViewId(stored) ? stored : DEFAULT_VIEW_ID;
  } catch {
    return DEFAULT_VIEW_ID;
  }
}

/**
 * Seletor de visão (filtro de apresentação, NUNCA controle de acesso — ver
 * `layout/views.ts`). Dropdown Radix: focus trap, `Escape` fecha, navegação
 * por teclado de graça. Nome acessível textual (rótulo da visão atual), não
 * só ícone.
 */
function ViewSwitcher({ view, onChange }: { view: ViewId; onChange: (id: ViewId) => void }): ReactElement {
  const current = getView(view);
  const Icon = current.icon;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          data-view-switcher="true"
          aria-label={`Visão: ${current.label}. Abrir seletor de visão`}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-control border border-border-strong bg-surface px-3 py-2 text-sm font-medium text-body transition-colors duration-fast ease-s2 hover:bg-surface-soft focus-visible:outline-none"
        >
          <Icon className="h-5 w-5" aria-hidden focusable={false} />
          <span className="hidden whitespace-nowrap lg:inline">{current.label}</span>
          <ChevronDown className="h-4 w-4" aria-hidden focusable={false} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-50 w-72 rounded-panel border border-border bg-surface p-1 shadow-s2"
        >
          <DropdownMenu.Label className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted">
            Visão (só apresentação — não é permissão)
          </DropdownMenu.Label>
          {VIEWS.map((item) => {
            const ItemIcon = item.icon;
            const selected = item.id === view;
            return (
              <DropdownMenu.Item
                key={item.id}
                data-view-option={item.id}
                onSelect={() => onChange(item.id)}
                className="flex cursor-pointer items-start gap-3 rounded-control px-3 py-2 text-sm text-body outline-none transition-colors duration-fast ease-s2 hover:bg-surface-soft focus:bg-surface-soft"
              >
                <ItemIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted" aria-hidden focusable={false} />
                <span className="flex flex-1 flex-col">
                  <span className="font-medium text-ink">{item.label}</span>
                  <span className="text-xs text-muted">{item.description}</span>
                </span>
                {selected ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-text" aria-hidden focusable={false} /> : null}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

interface SidebarProps {
  navId: string;
  mobileOpen: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  view: ViewId;
}

function Sidebar({ navId, mobileOpen, collapsed, onToggleCollapsed, view }: SidebarProps): ReactElement {
  const { pathname } = useLocation();
  const levelOf = useAccessLevelFor();
  const activeGroupId = NAV_GROUPS.find((group) => itemsFor(group, (path) => levelOf(path) !== "none").some((item) => isActivePath(item.to, pathname)))?.id;
  // Abertos por escolha do operador + o grupo da rota atual (abre ao navegar,
  // inclusive por link direto/reload). Fechar à mão um grupo ativo vale até a próxima navegação.
  const [openGroups, setOpenGroups] = useState<string[]>(activeGroupId ? [activeGroupId] : []);
  useEffect(() => {
    if (activeGroupId) setOpenGroups((current) => (current.includes(activeGroupId) ? current : [...current, activeGroupId]));
  }, [activeGroupId]);
  const toggleGroup = (id: string): void =>
    setOpenGroups((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  return h(
    "nav",
    {
      id: navId,
      "aria-label": "Módulos da Plataforma",
      "data-sidebar-open": mobileOpen ? "true" : "false",
      "data-sidebar-collapsed": collapsed ? "true" : "false",
      className:
        "fixed inset-y-0 left-0 z-40 flex w-[260px] flex-col bg-sidebar " +
        "transition-[transform,width] duration-base ease-s2 md:sticky md:top-0 md:h-screen md:translate-x-0 md:shrink-0 " +
        (collapsed ? "md:w-[76px] " : "md:w-64 ") +
        (mobileOpen ? "translate-x-0 shadow-s2" : "-translate-x-full md:shadow-none")
    },
    h("div", { className: "flex h-16 items-center border-b border-sidebar-border px-3" }, h(Brand, { collapsed })),
    h(
      "div",
      { className: "sidebar-scroll flex-1 overflow-y-auto px-3 py-4" },
      h("div", { className: "flex flex-col gap-5" }, NAV_GROUPS.filter((group) => groupVisibleInView(group.id, view)).map((group) => h(NavSection, { key: group.id, group, collapsed, open: openGroups.includes(group.id), onToggle: () => toggleGroup(group.id) })))
    ),
    // Selo discreto de identidade — reforça "staff" sem disputar atenção.
    collapsed
      ? null
      : h(
          "div",
          { className: "flex items-center gap-2 border-t border-sidebar-border px-4 py-3 text-xs text-sidebar-muted" },
          h(BadgeCheck, { className: "h-4 w-4 text-sidebar-fg", "aria-hidden": true, focusable: false }),
          h("span", null, "Acesso de staff")
        ),
    // Recolher/expandir (só desktop; no mobile o menu é off-canvas).
    h(
      "button",
      {
        type: "button",
        onClick: onToggleCollapsed,
        "aria-pressed": collapsed,
        "aria-label": collapsed ? "Expandir menu" : "Recolher menu",
        "data-sidebar-toggle": "true",
        className:
          "m-3 hidden min-h-[44px] items-center justify-center gap-2 rounded-control text-sm font-bold " +
          "text-sidebar-fg transition-colors duration-fast ease-s2 hover:bg-sidebar-hover hover:text-white " +
          "focus-visible:outline-none md:flex"
      },
      collapsed
        ? h(ChevronsRight, { className: "h-5 w-5", "aria-hidden": true, focusable: false })
        : h(
            "span",
            { className: "inline-flex items-center gap-2" },
            h(ChevronsLeft, { className: "h-5 w-5", "aria-hidden": true, focusable: false }),
            "Recolher"
          )
    )
  );
}

export interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps): ReactElement {
  const { session, signOut } = useStaffSession();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Recolhimento é só apresentação e preferência por navegador: SSR/primeiro
  // paint sempre expandido (todos os rótulos no HTML); a preferência salva é
  // aplicada após montar. localStorage pode lançar (janela privada) → try/catch.
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(COLLAPSE_KEY) === "1");
    } catch {
      /* sem storage: segue expandido */
    }
  }, []);
  function toggleCollapsed(): void {
    setCollapsed((value) => {
      const next = !value;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* preferência não persiste; o estado em memória basta */
      }
      return next;
    });
  }
  const navId = "app-shell-nav";
  const [view, setView] = useState<ViewId>(() => readStoredView());
  function onChangeView(next: ViewId): void {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Sem storage (modo privado): a visão some no reload; a troca na sessão continua valendo.
    }
  }

  return h(
    "div",
    { className: "app-shell flex min-h-screen bg-canvas text-body", "data-view": view },
    h("a", { href: "#conteudo", className: "skip-link" }, "Ir para o conteúdo"),
    // Backdrop do off-canvas em mobile (clicável para fechar). Escondido em >=md.
    mobileOpen
      ? h("button", {
          type: "button",
          "aria-label": "Fechar menu",
          onClick: () => setMobileOpen(false),
          className: "fixed inset-0 z-30 bg-black/40 md:hidden"
        })
      : null,
    h(Sidebar, { navId, mobileOpen, collapsed, onToggleCollapsed: toggleCollapsed, view }),
    h(
      "div",
      { className: "flex min-w-0 flex-1 flex-col" },
      // Faixa de ambiente (referência: "Ambiente de demonstração"). Só aparece
      // com sessão simulada — nunca em produção com Supabase configurado.
      session?.source === "simulator"
        ? h(
            "div",
            {
              className: "bg-sidebar px-4 py-1 text-center text-xs font-semibold text-sidebar-fg sm:text-sm",
              "data-env-banner": "simulator"
            },
            "Ambiente simulado · dados de exemplo"
          )
        : null,
      h(
        "header",
        {
          className:
            "sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-surface px-4"
        },
        // Botão de menu só em mobile; controla a sidebar off-canvas.
        h(
          "button",
          {
            type: "button",
            onClick: () => setMobileOpen((open) => !open),
            "aria-expanded": mobileOpen,
            "aria-controls": navId,
            "aria-label": mobileOpen ? "Fechar menu de módulos" : "Abrir menu de módulos",
            className:
              "inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-control " +
              "border border-border-strong text-body hover:bg-surface-soft focus-visible:outline-none md:hidden"
          },
          h(Menu, { className: "h-6 w-6", "aria-hidden": true, focusable: false })
        ),
        h("h1", { className: "whitespace-nowrap text-base font-extrabold text-ink md:text-lg" }, "Super2 Plataforma"),
        h(
          "div",
          { className: "ml-auto flex items-center gap-3" },
          session
            ? h(
                "p",
                { className: "app-shell__session hidden whitespace-nowrap text-sm text-body 2xl:block" },
                `${session.email} · ${session.role}`,
                session.source === "simulator"
                  ? h(
                      "span",
                      {
                        className:
                          "app-shell__badge ml-2 inline-flex items-center rounded-control border border-warning " +
                          "px-2 py-0.5 text-xs font-semibold text-warning",
                        "data-session-source": "simulator"
                      },
                      "sessão simulada (sem credencial Supabase configurada)"
                    )
                  : null
              )
            : null,
          h(ViewSwitcher, { view, onChange: onChangeView }),
          // Loja e Plataforma leem as mesmas tabelas (products/offers): o link só aparece quando o
          // ambiente informa o endereço da Loja, para nunca apontar para lugar nenhum.
          STORE_URL
            ? h(
                "a",
                {
                  href: STORE_URL,
                  target: "_blank",
                  rel: "noopener noreferrer",
                  "data-store-link": "true",
                  className:
                    "inline-flex min-h-[44px] items-center gap-2 rounded-control border border-border-strong " +
                    "bg-surface px-3 py-2 text-sm font-medium text-body transition-colors duration-fast ease-s2 " +
                    "hover:bg-surface-soft focus-visible:outline-none"
                },
                h(ExternalLink, { className: "h-5 w-5", "aria-hidden": true, focusable: false }),
                h("span", { className: "hidden sm:inline" }, "Ver loja")
              )
            : null,
          h(S2ThemeToggle),
          h(
            "button",
            {
              type: "button",
              onClick: signOut,
              "data-logout": "true",
              "aria-label": "Sair da Plataforma",
              className:
                "inline-flex min-h-[44px] items-center gap-2 rounded-control border border-border-strong " +
                "bg-surface px-3 py-2 text-sm font-medium text-body transition-colors duration-fast ease-s2 " +
                "hover:bg-surface-soft focus-visible:outline-none"
            },
            h(LogOut, { className: "h-5 w-5", "aria-hidden": true, focusable: false }),
            h("span", { className: "hidden sm:inline" }, "Sair")
          )
        )
      ),
      h("main", { className: "app-shell__main flex-1 px-4 py-6 md:px-6", id: "conteudo" }, children)
    )
  );
}
